import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useRef,
} from "react";

// 리스트 조회 Context
interface OptionListContextType {
  isSuccess: boolean;
  setIsSuccess: (isSuccess: boolean) => void;
  param: initialFilterStateProps | undefined;
  setParam: React.Dispatch<
    React.SetStateAction<initialFilterStateProps | undefined>
  >;
}

const OptionListContext = createContext<OptionListContextType | undefined>(
  undefined
);

interface OptionListProviderProps {
  children: ReactNode;
}

export const OptionListProvider = ({ children }: OptionListProviderProps) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [param, setParam] = useState<initialFilterStateProps | undefined>(
    undefined
  );

  return (
    <OptionListContext.Provider
      value={{
        isSuccess,
        setIsSuccess,
        param,
        setParam,
      }}
    >
      {children}
    </OptionListContext.Provider>
  );
};

export const useOptionListContext = (): OptionListContextType => {
  const context = useContext(OptionListContext);

  if (!context) {
    throw new Error(
      "useOptionListContext는 OptionListProvider 내부에서만 사용해야 합니다."
    );
  }

  return context;
};

// 추가정보 Context
interface OptionAddInfContextType {
  isSuccess: boolean;
  setIsSuccess: (isSuccess: boolean) => void;
  koValuesRef: React.MutableRefObject<null>;
  enValuesRef: React.MutableRefObject<null>;
  jaValuesRef: React.MutableRefObject<null>;
  getDefaultValue: (langCd: string) => any;
  resetDefaultValue: (langCd: string) => void;
}

const OptionAddInfContext = createContext<OptionAddInfContextType | undefined>(
  undefined
);

interface OptionAddInfProviderProps {
  children: ReactNode;
}

export const OptionAddInfProvider = ({
  children,
}: OptionAddInfProviderProps) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const koValuesRef = useRef(null);
  const enValuesRef = useRef(null);
  const jaValuesRef = useRef(null);

  const getDefaultValue = (langCd: string) => {
    return langCd === "ko"
      ? koValuesRef.current
      : langCd === "en"
      ? enValuesRef.current
      : langCd === "ja"
      ? jaValuesRef.current
      : undefined;
  };

  const resetDefaultValue = (langCd: string) => {
    if (langCd === "ko") {
      koValuesRef.current = null;
    }
    if (langCd === "en") {
      enValuesRef.current = null;
    }
  };

  return (
    <OptionAddInfContext.Provider
      value={{
        isSuccess,
        setIsSuccess,
        koValuesRef,
        enValuesRef,
        jaValuesRef,
        getDefaultValue,
        resetDefaultValue,
      }}
    >
      {children}
    </OptionAddInfContext.Provider>
  );
};

export const useOptionAddInfContext = (): OptionAddInfContextType => {
  const context = useContext(OptionAddInfContext);

  if (!context) {
    throw new Error(
      "useOptionAddInfContext는 OptionAddInfProvider 내부에서만 사용해야 합니다."
    );
  }

  return context;
};

// FilterForm Context
export type initialFilterStateProps = {
  rmNo: string;
  latChkOutTi: string;
  brkfInYn: string;
  uyn: string;
};
interface OptionFilterContextType {
  filters: initialFilterStateProps | undefined;
  onChange: (name: string, value: string) => void;
  onSelect: (name: string, value: string) => void;
  onReset: () => void;
}

const OptionFilterContext = createContext<OptionFilterContextType | undefined>(
  undefined
);

interface OptionFilterProviderProps {
  children: ReactNode;
}

export const OptionFilterProvider = ({
  children,
}: OptionFilterProviderProps) => {
  const initialFilterState = {
    rmNo: "",
    latChkOutTi: "",
    brkfInYn: "Y",
    uyn: "Y",
  };

  const [filters, setFilters] =
    useState<initialFilterStateProps>(initialFilterState);

  const onChange = (name: string, value: string) => {
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const onSelect = (name: string, value: string) => {
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const onReset = () => {
    setFilters(initialFilterState);
  };

  return (
    <OptionFilterContext.Provider
      value={{
        filters,
        onChange,
        onSelect,
        onReset,
      }}
    >
      {children}
    </OptionFilterContext.Provider>
  );
};

export const useOptionFilterContext = (): OptionFilterContextType => {
  const context = useContext(OptionFilterContext);

  if (!context) {
    throw new Error(
      "useOptionFilterContext는 OptionFilterProvider 내부에서만 사용해야 합니다."
    );
  }

  return context;
};
